import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Linkedin, CheckCircle, Loader2, Calendar, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "@/components/SEO";

import { useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALENDLY_URL = "https://calendly.com/official-magnuskor";

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiry_type: "",
    message: ""
  });

  useEffect(() => {
    if (!document.querySelector('script[src*="calendly"]')) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent! We'll be in touch soon.");
    } catch (error) {
      toast.error("Error sending message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How do I contact Magnus Kor Talent Partners?", "acceptedAnswer": { "@type": "Answer", "text": "You can reach us by email at recruitment@magnuskor.com, book a free consultation via our Calendly link, or fill out the contact form on our website. We respond within 24 hours." }},
      { "@type": "Question", "name": "Can I book a free consultation?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. We offer free 30-minute consultations to discuss your hiring needs, team structure, and talent strategy. No commitment required. Book directly through our website." }},
      { "@type": "Question", "name": "What is Magnus Kor's office address?", "acceptedAnswer": { "@type": "Answer", "text": "Our office is at 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom." }}
    ]
  };

  return (
    <div className="min-h-screen pt-24">
      <SEO 
        title="Contact Us - Get in Touch"
        description="Contact Magnus Kor Talent Partners to discuss hiring needs for your VC-backed SaaS startup or explore career opportunities. Book a free consultation today. London, UK."
        keywords="contact Magnus Kor, startup hiring consultation, talent advisory contact, tech recruitment inquiry, book hiring consultation, SaaS startup recruiter contact, London talent advisory"
        canonical="https://magnuskor.com/contact"
        jsonLd={contactFaqSchema}
      />
      {/* Header */}
      <section className="py-16 bg-white border-b border-charcoal-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gold-600 font-medium mb-4">Contact</p>
          <h1 className="font-serif text-4xl md:text-5xl text-charcoal-900 mb-4">
            Let's Connect
          </h1>
          <p className="text-lg text-charcoal-500 max-w-2xl">
            Whether you're looking to hire or exploring your next opportunity, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Calendly Booking Section */}
      <section className="py-16 bg-white" data-testid="calendly-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Calendar size={24} className="text-[#B8860B]" />
              <h2 className="font-display text-2xl text-gray-900">Book a Free Consultation</h2>
            </div>
            <p className="text-gray-500 max-w-lg mx-auto">
              Schedule a 30-minute call to discuss your hiring needs, team structure, and talent strategy. No commitment required.
            </p>
          </div>
          <div 
            className="calendly-inline-widget" 
            data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=faf9f6&text_color=1a1a1a&primary_color=b8860b`}
            style={{ minWidth: "320px", height: "700px" }}
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-2xl text-charcoal-900 mb-8">Get in Touch</h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-50 flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-gold-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal-900 mb-1">Email Us</h3>
                    <a href="mailto:recruitment@magnuskor.com" className="text-gold-600 hover:text-gold-700">
                      recruitment@magnuskor.com
                    </a>
                    <p className="text-sm text-charcoal-500 mt-1">For hiring inquiries</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="office-email">
                  <div className="w-10 h-10 bg-charcoal-100 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-charcoal-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal-900 mb-1">General Enquiries</h3>
                    <a href="mailto:office@magnuskor.com" className="text-gold-600 hover:text-gold-700">
                      office@magnuskor.com
                    </a>
                    <p className="text-sm text-charcoal-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-50 flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-gold-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal-900 mb-1">Locations</h3>
                    <p className="text-charcoal-600 text-sm">London, UK</p>
                    <p className="text-sm text-charcoal-500 mt-1">Working with clients globally</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-50 flex items-center justify-center flex-shrink-0">
                    <Linkedin size={20} className="text-gold-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-charcoal-900 mb-1">LinkedIn</h3>
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gold-600 hover:text-gold-700">
                      Follow us on LinkedIn
                    </a>
                    <p className="text-sm text-charcoal-500 mt-1">Stay updated on new opportunities</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-12 border-t border-charcoal-200">
                <h3 className="font-serif text-lg text-charcoal-900 mb-4">Looking for something specific?</h3>
                <div className="space-y-3">
                  <Link to="/for-startups" className="flex items-center gap-2 text-charcoal-600 hover:text-gold-600 transition-colors">
                    → I want to hire talent
                  </Link>
                  <Link to="/for-talent" className="flex items-center gap-2 text-charcoal-600 hover:text-gold-600 transition-colors">
                    → I'm looking for a new role
                  </Link>
                  <Link to="/jobs" className="flex items-center gap-2 text-charcoal-600 hover:text-gold-600 transition-colors">
                    → Browse open positions
                  </Link>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="bg-white p-12 text-center">
                  <CheckCircle size={48} className="text-gold-500 mx-auto mb-4" />
                  <h3 className="font-serif text-2xl text-charcoal-900 mb-2">Message Sent</h3>
                  <p className="text-charcoal-500 mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: "", email: "", inquiry_type: "", message: "" });
                    }}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10" data-testid="contact-form">
                  <h2 className="font-serif text-xl text-charcoal-900 mb-6">Send a Message</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-charcoal-700 mb-2">Your Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 border border-charcoal-200 focus:border-gold-500 focus:outline-none"
                        data-testid="contact-name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal-700 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-charcoal-200 focus:border-gold-500 focus:outline-none"
                        data-testid="contact-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal-700 mb-2">I'm interested in... *</label>
                      <select
                        required
                        value={formData.inquiry_type}
                        onChange={(e) => setFormData({...formData, inquiry_type: e.target.value})}
                        className="w-full px-4 py-3 border border-charcoal-200 focus:border-gold-500 focus:outline-none bg-white"
                        data-testid="contact-type"
                      >
                        <option value="">Select an option</option>
                        <option value="client">Hiring talent for my company</option>
                        <option value="candidate">Finding my next role</option>
                        <option value="partnership">Partnership opportunity</option>
                        <option value="general">General inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-charcoal-700 mb-2">Message *</label>
                      <textarea
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full px-4 py-3 border border-charcoal-200 focus:border-gold-500 focus:outline-none resize-none"
                        placeholder="Tell us how we can help..."
                        data-testid="contact-message"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full mt-8 flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
